import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  Plan,
  PlanDetails,
  PlanEntitlement,
  PlanEntitlementKey,
  PlanLicense,
  PlanLicenseType,
  useGetPlanDetailsQuery,
  useListPlansQuery,
} from '../../api/graphql/generated';
import { useIsFeatureBetaUserLazy } from '../../api/hooks/feature.hooks';

const useGetPlanSubscription = (): PlanDetails | undefined => {
  const { data: planDetailsResponse } = useGetPlanDetailsQuery({
    fetchPolicy: 'cache-only',
  });

  return planDetailsResponse?.getPlanDetails as PlanDetails | undefined;
};

const useGetPlanSubscriptionEntitlements = (): PlanEntitlement[] => {
  const subscription = useGetPlanSubscription();

  const entitlements: PlanEntitlement[] = useMemo(() => {
    return subscription?.entitlements ?? [];
  }, [subscription?.entitlements]);

  return entitlements;
};

const useGetPlanSubscriptionLicenses = (): PlanLicense[] => {
  const subscription = useGetPlanSubscription();

  const licenses: PlanLicense[] = useMemo(() => {
    return subscription?.licenses ?? [];
  }, [subscription?.licenses]);

  return licenses;
};

/**
 * Helper hook to isolate logic to detect if a trial is available.
 */
const useListTrialablePlans = (): Array<Plan> => {
  const { data: plans } = useListPlansQuery({
    variables: {
      input: {
        trialAvailable: true,
      },
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-only',
  });
  return plans?.listPlans?.items ?? [];
};

/**
 * Helper hook to isolate logic to detect if a trial is available.
 */
const usePlanIsTrialAvailable = (options?: {
  /**
   * Check if this plan is trialable by the tenant.
   * If not specified then checks if any plan is trialable.
   */
  planId: string;
}): boolean => {
  const { planId } = options ?? {};
  const trialablePlans = useListTrialablePlans();
  if (planId) {
    return trialablePlans.some((plan) => plan.id === planId);
  }
  return trialablePlans.length > 0;
};

interface HasAssignedPlanLicenseOptions {
  /**
   * The entitlement to check is licensed.
   */
  key: PlanEntitlementKey;
  /**
   * The entity to check that the entitlement is licensed to.
   * For example, a machine id.
   */
  assignedTo: string;
}

type HasAssignedPlanLicenseCallback = (
  options: HasAssignedPlanLicenseOptions
) => boolean;

/**
 * Checks if a machine has a license for a plan that includes the required entitlement.
 * For example, to use multipass welding, a machine needs to be licensed for the multipass plan.
 */
const useHasAssignedPlanLicense = (
  options: HasAssignedPlanLicenseOptions
): boolean => {
  const isAssignedLicensed = useHasAssignedPlanLicenseLazy();
  return isAssignedLicensed(options);
};

const useHasAssignedPlanLicenseLazy = (): HasAssignedPlanLicenseCallback => {
  const isBetaUser = useIsFeatureBetaUserLazy();
  const licenses = useGetPlanSubscriptionLicenses();

  return useCallback<HasAssignedPlanLicenseCallback>(
    (options: HasAssignedPlanLicenseOptions): boolean => {
      const { key, assignedTo } = options;

      // As a beta user for this feature, you are entitled for it,
      // regardless if the tenant or machine is licensed for it.
      if (isBetaUser(key)) {
        return true;
      }

      // You have no licenses, so you can't be licensed for anything.
      if (isEmpty(licenses)) {
        return false;
      }

      // Find the first license that has the entitlement.
      const license = licenses.find((license) => {
        return license.plan?.entitlements?.some((entitlement) => {
          return entitlement.key === key;
        });
      });

      // You have some licenses, but not the one for the entitlement you seek.
      if (!license) {
        return false;
      }

      // The license applies to all things under the tenant.
      // No further checks needed. Congrats!
      if (license.plan.licenseType === PlanLicenseType.TENANT) {
        return true;
      }

      const isAssigned = license.assignments?.some((assignment) => {
        return assignment.assignedTo === assignedTo;
      });

      return isAssigned ?? false;
    },
    [licenses, isBetaUser]
  );
};

export {
  useGetPlanSubscription,
  useListTrialablePlans,
  usePlanIsTrialAvailable,
  useGetPlanSubscriptionEntitlements,
  useGetPlanSubscriptionLicenses,
  useHasAssignedPlanLicense,
  useHasAssignedPlanLicenseLazy,
};
